import type { GengageChat } from './runtime.js';

export const CHAT_CONVERSATION_RESET_EVENT = 'gengage:chat:conversation-reset' as const;

/** Identifies which SDK-owned lifecycle path cleared the active conversation. */
export type ChatConversationResetReason = 'new_chat' | 'page_context_changed' | 'history_session_changed';

export interface ChatConversationResetDetail {
  reason: ChatConversationResetReason;
}

interface ConversationResettableChat {
  _resetForNewPage(reason?: ChatConversationResetReason): void;
}

/**
 * Clear a chat instance without opening it or sending a backend request.
 *
 * This dedicated subpath deliberately leaves the long-lived GengageChat and
 * ChatPublicAPI shapes unchanged. CSH always consumes the latest FE SDK, while
 * otokoc-assistants is the initial downstream client of this lifecycle seam.
 */
export function resetChatConversation(chat: GengageChat): void {
  const reset = (chat as unknown as ConversationResettableChat)._resetForNewPage;
  if (typeof reset !== 'function') {
    throw new TypeError('resetChatConversation requires a GengageChat instance');
  }
  reset.call(chat, 'new_chat');
}

/** Subscribe to SDK-owned conversation replacements. */
export function listenForChatConversationReset(handler: (detail: ChatConversationResetDetail) => void): () => void {
  const listener = (event: Event) => {
    handler((event as CustomEvent<ChatConversationResetDetail>).detail);
  };
  window.addEventListener(CHAT_CONVERSATION_RESET_EVENT, listener);
  return () => window.removeEventListener(CHAT_CONVERSATION_RESET_EVENT, listener);
}
